import ActionCard from '@precis-digital/kurama/ActionCard';
import ThemedAvatar from 'shared/components/Themed/Avatar';
import Button from '@precis-digital/kurama/Button';
import Grid from '@precis-digital/kurama/Grid';
import Input from '@precis-digital/kurama/Input';
import SingleSelectDropdown, { type SingleSelectDropdownItem } from '@precis-digital/kurama/SingleSelectDropdown';
import Typography from '@precis-digital/kurama/Typography';
import { styled } from '@precis-digital/kurama/utils';
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useCopilot } from 'shared/context/CopilotContext';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useAlvieDatasourceOptions } from 'shared/copilot/hooks/useAlvieDatasourceOptions';
import { useTranslation } from 'shared/translations';
import { getPlatformDetailsByPlatform } from 'src/dataSource/utils';
import ThemedChip from 'shared/components/Themed/Chip';
import { filterOptionsForSuggestDataSource, getPlatformForSuggestDataSourceType, getSuggestDataSourceTypeLabel, parseSuggestDataSourceArgs } from './suggestDataSource';
export interface DataSourceSuggestionCardProps {
  args: Record<string, unknown>;
}

/**
 * Flow-parity card for `suggest_data_source`, built on Kurama `ActionCard`.
 */
const DataSourceSuggestionCard = ({
  args
}: DataSourceSuggestionCardProps): ReactElement => {
  const {
    t
  } = useTranslation('common', {
    keyPrefix: 'copilot.dataAgent.suggestDataSource'
  });
  const {
    setSelectedDatasources,
    sendUserMessage
  } = useCopilot();
  const {
    currentClient
  } = useCurrentClient();
  const {
    options,
    isLoading
  } = useAlvieDatasourceOptions(currentClient.id);
  const {
    sourceType,
    reasoning,
    suggestedPrompt
  } = parseSuggestDataSourceArgs(args);
  const sourceTypeLabel = getSuggestDataSourceTypeLabel(sourceType);
  const suggestedPlatform = getPlatformForSuggestDataSourceType(sourceType);
  const suggestedPlatformDetails = suggestedPlatform != null ? getPlatformDetailsByPlatform(suggestedPlatform) : undefined;
  const matchingOptions = useMemo(() => filterOptionsForSuggestDataSource(options, sourceType), [options, sourceType]);
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(undefined);
  const [messageText, setMessageText] = useState(suggestedPrompt);
  const [confirmedLabel, setConfirmedLabel] = useState<string | null>(null);
  const pendingSendRef = useRef<{
    message: string;
    label: string;
  } | null>(null);
  const [pendingSendId, setPendingSendId] = useState(0);
  useEffect(() => {
    if (matchingOptions.length === 1 && selectedOptionId == null) {
      setSelectedOptionId(matchingOptions[0].id);
    }
  }, [matchingOptions, selectedOptionId]);
  useEffect(() => {
    const pending = pendingSendRef.current;
    if (pending == null) return;
    pendingSendRef.current = null;
    const message = pending.message.trim();
    if (message.length === 0) {
      setConfirmedLabel(pending.label);
      return;
    }

    // A canned confirmation, not the user's own text — it must never pick
    // up whatever the user has staged in the composer for their next
    // question. Only lock the connected chip once the agent accepts it;
    // a drop while the agent is still running leaves the card retryable.
    const sent = sendUserMessage(message, {
      includeComposerAttachments: false
    });
    if (sent) {
      setConfirmedLabel(pending.label);
    }
  }, [pendingSendId, sendUserMessage]);
  const dropdownOptions: SingleSelectDropdownItem[] = useMemo(() => matchingOptions.map(option => {
    const details = getPlatformDetailsByPlatform(option.platform);
    return {
      value: option.id,
      label: option.label,
      annotation: option.subLabel,
      icon: details?.iconUrl != null ? <ThemedAvatar kind="image" imageUrl={details.iconUrl} size="small" /> : undefined
    };
  }), [matchingOptions]);
  const selectedOption = matchingOptions.find(option_0 => option_0.id === selectedOptionId) ?? matchingOptions[0];
  const selectedPlatformDetails = selectedOption != null ? getPlatformDetailsByPlatform(selectedOption.platform) : undefined;
  const handleStartAnalysis = (): void => {
    if (selectedOptionId == null) return;
    const selected = matchingOptions.find(option_1 => option_1.id === selectedOptionId);
    if (selected == null) return;
    pendingSendRef.current = {
      message: messageText,
      label: selected.label
    };
    setSelectedDatasources([selected.id], options);
    setPendingSendId(id => id + 1);
  };
  if (confirmedLabel != null) {
    return <ThemedChip avatarSrc={selectedPlatformDetails?.iconUrl} label={t('connectedChip', {
      name: confirmedLabel
    })} variant="semantic-success" />;
  }
  const canStartAnalysis = selectedOptionId != null && !isLoading;
  return <ActionCard title={t('title')} variant="semantic-info" description={<StyledDescription>
          <StyledSuggestedType>
            {t('suggestedType', {
        sourceType: sourceTypeLabel
      })}
            {suggestedPlatformDetails?.iconUrl != null && suggestedPlatformDetails.iconUrl.length > 0 ? <ThemedAvatar kind="image" imageUrl={suggestedPlatformDetails.iconUrl} size="small" /> : null}
          </StyledSuggestedType>

          {reasoning.trim().length > 0 ? <Typography variant="body2" color="secondary">
              {reasoning}
            </Typography> : null}

          <StyledSection>
            <Typography variant="body3" color="secondary">
              {t('dataSourceLabel')}
            </Typography>
            {isLoading ? <Typography variant="body2" color="secondary">
                {t('loadingAccounts')}
              </Typography> : matchingOptions.length === 0 ? <Typography variant="body2" color="secondary">
                {t('noAccounts', {
          sourceType: sourceTypeLabel
        })}
              </Typography> : <SingleSelectDropdown title={t('selectAccountPlaceholder')} buttonTitle={t('selectAccountPlaceholder')} options={dropdownOptions} value={selectedOptionId} allowUndefinedValue onSelect={value => {
        setSelectedOptionId(value);
      }} buttonWidth="100%" dropdownMinWidth="280px" includeSearch={matchingOptions.length > 5} />}
          </StyledSection>

          {suggestedPrompt.trim().length > 0 ? <StyledSection>
              <Typography variant="body3" color="secondary">
                {t('startWithMessageLabel')}
              </Typography>
              <Input name="suggest-data-source-prompt" value={messageText} onChange={event => {
        setMessageText(event.target.value);
      }} multiline minRows={2} maxRows={6} fullWidth />
            </StyledSection> : null}
        </StyledDescription>} actions={[<Button key="start-analysis" variant="filled" disabled={!canStartAnalysis} onClick={handleStartAnalysis}>
          {t('startAnalysis')}
        </Button>]} />;
};
const StyledDescription = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  width: '100%'
}));
const StyledSuggestedType = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
  ...theme.typography.h4
}));
const StyledSection = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  width: '100%'
}));
export default DataSourceSuggestionCard;